import React, { useEffect, useMemo, useRef, useState } from 'react';
import { whatsappApi, formatApiError } from '../services/api';
import { Button } from './ui/button';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from './ui/sheet';
import { toast } from 'sonner';
import { WhatsappLogo, PaperPlaneTilt, Sparkle, Warning } from '@phosphor-icons/react';

const applyTemplate = (body, customer) => {
  if (!body) return '';
  const first = customer?.first_name || '';
  return body
    .replace(/\{first_name\}/g, first)
    .replace(/\{last_name\}/g, customer?.last_name || '')
    .replace(/\{[a-z_]+\}/g, '');
};

const formatTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('de-DE');
};

const WhatsAppChatDrawer = ({ open, onOpenChange, customer, onSent }) => {
  const [messages, setMessages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [status, setStatus] = useState(null);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!open || !customer) return;
    (async () => {
      setLoading(true);
      try {
        const [st, tpl, thr] = await Promise.all([
          whatsappApi.status(),
          whatsappApi.templates(),
          whatsappApi.thread(customer.id),
        ]);
        setStatus(st.data);
        setTemplates(tpl.data?.templates || []);
        setMessages(thr.data?.messages || []);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [open, customer]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const configured = status?.configured;
  const hasPhone = !!customer?.phone;

  const applyTpl = (t) => {
    setText(applyTemplate(t.body, customer));
  };

  const send = async () => {
    const body = (text || '').trim();
    if (!body) return;

    if (!configured) {
      toast.error('WhatsApp nicht konfiguriert');
      return;
    }

    if (!hasPhone) {
      toast.error('Kunde hat keine Telefonnummer');
      return;
    }

    setSending(true);
    try {
      const res = await whatsappApi.send({
        customer_id: customer.id,
        body
      });
      setMessages((prev) => [...prev, res.data.message]);
      setText('');
      toast.success('WhatsApp gesendet');
      onSent && onSent();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const groups = useMemo(() => {
    const out = [];
    let lastDate = '';

    for (const m of messages) {
      const d = formatDate(m.created_at);

      if (d !== lastDate) {
        out.push({ divider: d });
        lastDate = d;
      }

      out.push({ msg: m });
    }

    return out;
  }, [messages]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="p-0 w-full sm:max-w-md flex flex-col"
        data-testid="whatsapp-chat-drawer"
      >
        <SheetHeader className="p-4 border-b border-[#E2E4E9] bg-[#075E54] text-white">
          <SheetTitle className="text-white flex items-center gap-2">
            <WhatsappLogo
              weight="fill"
              size={22}
              className="text-[#25D366]"
            />
            WhatsApp Chat
          </SheetTitle>

          <SheetDescription className="text-white/80 text-xs">
            {customer?.first_name} {customer?.last_name} · {customer?.phone || 'keine Nummer'}
          </SheetDescription>
        </SheetHeader>

        {!configured && (
          <div className="p-3 bg-amber-50 border-b border-amber-200 text-amber-800 text-xs flex items-start gap-2">
            <Warning weight="fill" size={16} className="mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold">WhatsApp Business noch nicht eingerichtet</p>
              <p className="mt-0.5">
                Bitte in{' '}
                <a
                  href="/settings/integrations/whatsapp"
                  className="underline font-medium hover:text-amber-900"
                  data-testid="wa-drawer-settings-link"
                >
                  Einstellungen → Integrationen → WhatsApp
                </a>
                {' '}konfigurieren.
              </p>
            </div>
          </div>
        )}

        {configured && !hasPhone && (
          <div className="p-3 bg-red-50 border-b border-red-200 text-red-800 text-xs">
            Kunde hat keine Telefonnummer hinterlegt.
          </div>
        )}

        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto p-4 space-y-2 bg-[#ECE5DD]"
          data-testid="whatsapp-messages"
        >
          {loading && (
            <p className="text-center text-sm text-[#525866]">
              Lade …
            </p>
          )}

          {!loading && messages.length === 0 && (
            <p className="text-center text-sm text-[#525866] mt-8">
              Noch keine Nachrichten mit diesem Kunden.
            </p>
          )}

          {groups.map((g, i) => {
            if (g.divider) {
              return (
                <div key={`d-${i}`} className="flex justify-center">
                  <span className="bg-white/70 rounded-full px-3 py-0.5 text-[10px] text-[#525866] font-medium">
                    {g.divider}
                  </span>
                </div>
              );
            }

            const m = g.msg;
            const isOut = m.direction === 'outbound';

            return (
              <div
                key={m.id || m.sid}
                className={`flex ${isOut ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-lg px-3 py-2 text-sm shadow-sm ${
                    isOut
                      ? 'bg-[#DCF8C6] text-[#111418]'
                      : 'bg-white text-[#111418]'
                  }`}
                >
                  <p className="whitespace-pre-wrap break-words">
                    {m.body}
                  </p>

                  {m.media_urls?.length > 0 && (
                    <ul className="text-[11px] mt-1 space-y-0.5">
                      {m.media_urls.map((u, j) => (
                        <li key={j}>
                          <a
                            href={u}
                            target="_blank"
                            rel="noreferrer"
                            className="underline"
                          >
                            Anhang {j + 1}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}

                  <p className="text-[10px] text-[#525866] text-right mt-0.5">
                    {formatTime(m.created_at)}
                    {isOut && m.status ? ` · ${m.status}` : ''}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {templates.length > 0 && (
          <div className="border-t border-[#E2E4E9] p-2 bg-[#F8F9FB] overflow-x-auto flex gap-1">
            {templates.map((t) => (
              <button
                key={t.id || t.key}
                type="button"
                onClick={() => applyTpl(t)}
                className="whitespace-nowrap text-[11px] px-2 py-1 bg-white border border-[#E1E4E8] rounded hover:bg-[#F1F3F7] flex items-center gap-1"
                data-testid={`wa-template-${t.key}`}
              >
                <Sparkle weight="bold" size={11} /> {t.label}
              </button>
            ))}
          </div>
        )}

        <div className="border-t border-[#E2E4E9] p-3 bg-white flex items-end gap-2">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            rows={2}
            placeholder="Nachricht schreiben …"
            className="flex-1 resize-none border border-[#E2E4E9] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#111418]"
            disabled={!configured || !hasPhone || sending}
            data-testid="whatsapp-input"
          />

          <Button
            type="button"
            onClick={send}
            disabled={!configured || !hasPhone || sending || !text.trim()}
            data-testid="whatsapp-send-btn"
            className="bg-[#25D366] hover:bg-[#128C7E] text-white"
          >
            <PaperPlaneTilt weight="fill" size={16} />
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default WhatsAppChatDrawer;